import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1778942855277-0e9ccbf05751?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxhdXRvJTIwbWVjaGFuaWMlMjB3b3JraW5nJTIwbW9kZXJuJTIwc2hvcHxlbnwwfHx8fDE3ODM3MTQzOTB8MA&ixlib=rb-4.1.0&q=85";

function GoogleButton() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      type="button"
      variant="outline"
      data-testid="google-login-button"
      onClick={handleGoogle}
      className="w-full h-11 bg-slate-950 border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
    >
      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/>
      </svg>
      Continue with Google
    </Button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Hero */}
      <div className="hidden lg:block relative w-1/2 overflow-hidden">
        <img src={HERO} alt="Auto repair shop" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
        <div className="absolute bottom-0 left-0 p-12">
          <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew" className="h-10 object-contain mb-5" data-testid="login-hero-logo" />
          <h2 className="font-head font-black text-4xl text-slate-50 leading-tight max-w-md">
            Run your shop from the desk.
          </h2>
          <p className="text-slate-400 mt-3 max-w-md">
            Invoices, estimates, customers and cash flow — synced with your mobile app in real time.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew" className="h-9 object-contain" data-testid="login-logo" />
          </div>
          <h1 className="font-head font-bold text-2xl text-slate-50">Sign in</h1>
          <p className="text-slate-500 text-sm mt-1 mb-7">Access your shop dashboard.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" required value={email} data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)} placeholder="you@shop.com"
                className="h-11 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} required value={password} data-testid="login-password-input"
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="h-11 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50 pr-10" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-800" />
            <span className="text-xs text-slate-600 uppercase tracking-wide">or</span>
            <div className="h-px flex-1 bg-slate-800" />
          </div>
          <GoogleButton />

          <p className="text-sm text-slate-500 text-center mt-7">
            New here?{" "}
            <Link to="/register" data-testid="go-to-register" className="text-amber-400 hover:text-amber-300 font-medium">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
